import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiCall from "../../common/ApiCall";
import { setTranslationCookie } from "../../common/TranslationHelper";
import {
  getAffiliateRef,
  clearAffiliateRef,
} from "../../common/affiliateTracking";
import {
  getAuthPageSessionKey,
  clearAuthPageSession,
} from "../../common/authPageTracking";

const initialState = {
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isSocialLogin: false,
  socialLoginProvider: null,
  isRegisterLoading: false,
  error: null,
  activeForm: null,
  _hydrated: false,
  locationUpdated: false,
  countsUpdated: false,
  countsAdminUpdated: false,
  isImpersonating: false,
  hasBusinessAccount: null, // null = not yet loaded, false = no business account, true = has business account
  businessOnboardingStatus: null, // null = not yet loaded, 'completed', 'pending', etc.
  adminVisibility: true,
};

/**
 * Drop every trace of an impersonation session.
 *
 * `is_impersonating` gates real UI (most visibly, it hides the whole dream-entry
 * form), so a leftover flag silently breaks the app for a legitimately logged-in
 * user. Centralised here so the login and logout paths cannot disagree.
 */
const clearImpersonationState = (state) => {
  if (state) {
    state.isImpersonating = false;
  }
  localStorage.removeItem("is_impersonating");
  localStorage.removeItem("impersonated_user");
  localStorage.removeItem("admin_backup_user");
  localStorage.removeItem("admin_backup_token");
  localStorage.removeItem("admin_backup_has_business_account");
  localStorage.removeItem("admin_backup_business_onboarding_status");
};

// Helper function to load initial state from localStorage
const loadInitialState = () => {
  const token = localStorage.getItem("authToken");
  const user = JSON.parse(localStorage.getItem("user"));
  const isImpersonating = localStorage.getItem("is_impersonating") === "1";
  const hasBusinessAccountStr = localStorage.getItem("has_business_account");
  const businessOnboardingStatus = localStorage.getItem(
    "business_onboarding_status",
  );

  // Only set to true if explicitly 'true', otherwise null (not yet loaded) or false
  const hasBusinessAccount =
    hasBusinessAccountStr === "true"
      ? true
      : hasBusinessAccountStr === "false"
        ? false
        : null;

  return {
    ...initialState,
    token: token,
    user: user,
    isAuthenticated: !!token,
    _hydrated: true,
    isImpersonating: isImpersonating,
    hasBusinessAccount: hasBusinessAccount,
    businessOnboardingStatus: businessOnboardingStatus || null,
    twofaRequired: false,
    pendingUserId: null,
    pendingEmail: null,
  };
};

// Async Thunks
export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async ({ email, type }, { rejectWithValue }) => {
    try {
      const response = await ApiCall("post", "verify-email", { email, type });

      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ login, password, isAdmin = false }, { rejectWithValue }) => {
    try {
      const endpoint = isAdmin ? "admin/login" : "login";

      // Attributes the Login / Registration page visit that led here.
      const authSessionKey = getAuthPageSessionKey();
      const payload = { login, password };
      if (authSessionKey) {
        payload.auth_session_key = authSessionKey;
      }

      const response = await ApiCall("post", endpoint, payload);
      if (!response.data.success) {
        return rejectWithValue(response.data.data || response.data.message);
      }

      if (response.data.data.twofa_required) {
        // The session is deliberately kept — the login isn't complete until the
        // OTP is verified, and that step attributes the visit.
        return {
          twofa_required: true,
          user_id: response.data.data.user_id,
          email: response.data.data.email,
        };
      }

      clearAuthPageSession();

      // Store both token and user data in localStorage
      localStorage.setItem("authToken", response.data.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.data.user));
      return {
        token: response.data.data.access_token,
        user: response.data.data.user, // Make sure this is included
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateUser = createAsyncThunk(
  "auth/updateUser",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await ApiCall("POST", "update-profile", payload, token);

      if (!response.data.success) {
        return rejectWithValue(response.data.data || response.data.message);
      }

      // Update localStorage
      const currentUser = JSON.parse(localStorage.getItem("user"));
      let dataToMerge = response.data.data;
      if (response.data.data?.requires_otp) {
        const { twofa_status, ...rest } = response.data.data;
        dataToMerge = rest;
      }
      const updatedUser = { ...currentUser, ...dataToMerge };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return response.data.data;
    } catch (error) {
      if (error.response?.data?.data) {
        return rejectWithValue(error.response.data.data);
      }

      // Check if it's a message-based error
      if (error.response?.data?.message) {
        return rejectWithValue(
          error.response?.data || { message: error.message },
        );
      }

      // Fallback to generic error
      return rejectWithValue(error.message || "An error occurred");
    }
  },
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      // Format the request data
      const requestData = {
        first_name: payload.first_name,
        last_name: payload.last_name,
        display_name: payload.display_name,
        gender: payload.gender,
        phone: payload.phone,
        dob: `${payload.dob_year}-${payload.dob_month}-${payload.dob_day}`,
        location: payload.location,
        coordinates: payload.coordinates,
        city: payload.city,
        country: payload.country,
        avatar: payload.avatar,
        native_language: payload.native_language,
      };

      const response = await ApiCall(
        "POST",
        "complete-profile",
        requestData,
        token,
      );

      if (!response.data.success) {
        return rejectWithValue(response.data);
      }

      // Get current user from localStorage
      const currentUser = JSON.parse(localStorage.getItem("user"));

      // Create updated user object with nested user data from response
      const updatedUser = {
        ...currentUser,
        ...response.data.data.user,
      };

      // Update localStorage
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Return the updated data to reducer
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updatePrivacy = createAsyncThunk(
  "auth/updatePrivacy",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await ApiCall("POST", "update-privacy", payload, token);

      if (!response.data || !response.data.data) {
        return rejectWithValue("Invalid response from server");
      }

      // Update only `privacy` in localStorage
      const currentUser = JSON.parse(localStorage.getItem("user"));
      const updatedUser = {
        ...currentUser,
        privacy: response.data.data,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const updateNotificationSettings = createAsyncThunk(
  "auth/updateNotificationSettings",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { token } = getState().auth;
      const response = await ApiCall(
        "POST",
        "update-notification-settings",
        payload,
        token,
      );

      if (!response.data || !response.data.data) {
        return rejectWithValue("Invalid response from server");
      }

      // Update `privacy` in localStorage as notification settings are in privacy object
      const currentUser = JSON.parse(localStorage.getItem("user"));
      const updatedUser = {
        ...currentUser,
        privacy: response.data.data,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const socialLogin = createAsyncThunk(
  "auth/socialLogin",
  async ({ provider, token }, { rejectWithValue }) => {
    try {
      const endpoints = {
        google: "google-login",
        microsoft: "microsoft-login",
        facebook: "facebook-login",
      };

      // Attributes the Login / Registration page visit that led here. The
      // backend decides whether this counts as a login or a registration,
      // depending on whether the social sign-in created the account.
      const authSessionKey = getAuthPageSessionKey();
      const payload = { token };
      if (authSessionKey) {
        payload.auth_session_key = authSessionKey;
      }

      const response = await ApiCall("post", endpoints[provider], payload);

      if (!response.data.success) {
        return rejectWithValue(response.data.data || response.data.message);
      }

      clearAuthPageSession();

      localStorage.setItem("authToken", response.data.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.data.user));

      return {
        token: response.data.data.access_token,
        user: response.data.data.user,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

export const registerUser = createAsyncThunk(
  "auth/register",
  async (userData, { rejectWithValue }) => {
    try {
      // Attribute the signup when the visitor arrived via an affiliate link.
      const affiliateCode = getAffiliateRef();
      const payload = { ...userData };
      if (affiliateCode) {
        payload.affiliate_code = affiliateCode;
      }

      // Attributes the Login / Registration page visit that led here.
      const authSessionKey = getAuthPageSessionKey();
      if (authSessionKey) {
        payload.auth_session_key = authSessionKey;
      }

      const response = await ApiCall("post", "register", payload);
      if (!response.data.success) {
        return rejectWithValue(response.data.data || response.data.message);
      }

      if (affiliateCode) {
        clearAffiliateRef();
      }
      clearAuthPageSession();

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: loadInitialState(),
  reducers: {
    setActiveForm: (state, action) => {
      state.activeForm = action.payload;
    },
    clearErrors: (state) => {
      state.error = null;
    },
    logout: (state) => {
      // Clear translation cookie
      setTranslationCookie("en");

      // Note: intentionally NOT removing the Google Maps <script> tag or
      // `window.google` here. @react-google-maps/api's useJsApiLoader loads
      // the Maps JS API through a page-lifetime singleton (Loader.instance)
      // that only ever loads the script once and never re-injects it. Ripping
      // out the script/window.google on logout (without a full page reload)
      // desyncs that singleton from reality: on next login it still reports
      // "loaded" and skips reloading, while window.google is gone, causing
      // every subsequent map to fail with "Map could not be loaded."

      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("has_business_account");
      localStorage.removeItem("business_onboarding_status");
      if (state.user?.id) {
        localStorage.removeItem(`dreamComparisonState_${state.user.id}`);
      }
      localStorage.removeItem("dreamComparisonState");
      state.token = null;
      state.user = null;
      state.activeForm = null;
      state.isAuthenticated = false;
      state.hasBusinessAccount = null;
      state.businessOnboardingStatus = null;
      clearImpersonationState(state);
    },
    resetFormState: (state) => {
      state.activeForm = null;
      state.error = null;
    },
    rehydrate: (state) => {
      const token = localStorage.getItem("authToken");
      const user = JSON.parse(localStorage.getItem("user"));
      state.token = token;
      state.user = user;
      state.isAuthenticated = !!token;
      state._hydrated = true;
    },
    setLocationUpdated: (state, action) => {
      state.locationUpdated = action.payload;
    },
    setCountsUpdated: (state, action) => {
      state.countsUpdated = action.payload;
    },
    setAdminCountsUpdated: (state, action) => {
      state.countsAdminUpdated = action.payload;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      localStorage.setItem("user", JSON.stringify(action.payload));
      if (!action.payload) {
        localStorage.removeItem("authToken"); // optional if logging out
      }
    },
    setImpersonating(state, action) {
      state.isImpersonating = action.payload;
    },
    setBusinessAccount(state, action) {
      if (typeof action.payload === "object" && action.payload !== null) {
        const hasAccount =
          !!action.payload.id || action.payload.hasAccount === true;
        state.hasBusinessAccount = hasAccount;
        localStorage.setItem(
          "has_business_account",
          hasAccount ? "true" : "false",
        );

        if (action.payload.onboarding_status !== undefined) {
          state.businessOnboardingStatus = action.payload.onboarding_status;
          localStorage.setItem(
            "business_onboarding_status",
            action.payload.onboarding_status || "",
          );
        }
      } else {
        state.hasBusinessAccount = action.payload;
        localStorage.setItem(
          "has_business_account",
          action.payload ? "true" : "false",
        );
        // A bare boolean carries no onboarding info, so any previously stored
        // status is stale and must not leak across users (e.g. impersonation)
        // or sessions — otherwise a stale "completed" makes the Business tab
        // look active for an account that never finished onboarding.
        state.businessOnboardingStatus = null;
        localStorage.removeItem("business_onboarding_status");
      }
    },
    loginUserSuccess: (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.twofaRequired = false; // clear OTP mode
      localStorage.setItem("authToken", action.payload.token);
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      // A fresh login is never an impersonation. Impersonation is started in
      // four places but only cleared on the header Exit button, so exiting any
      // other way (closing the tab, logging out from the ads screens) used to
      // strand the flag — which then hid the dream-entry form for the next
      // person to log in on this browser.
      clearImpersonationState(state);
    },
    setAdminVisibility: (state, action) => {
      state.adminVisibility = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Verify Email
      .addCase(verifyEmail.pending, (state, action) => {
        const { type } = action.meta.arg;
        if (type === 0) {
          state.isLoading = true;
        } else if (type === 1) {
          state.isRegisterLoading = true;
        }
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        const { type } = action.meta.arg;
        if (type === 0) {
          state.isLoading = false;
        } else if (type === 1) {
          state.isRegisterLoading = false;
        }
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        const { type } = action.meta.arg;
        if (type === 0) {
          state.isLoading = false;
        } else if (type === 1) {
          state.isRegisterLoading = false;
        }
        state.error = action.payload;
      })

      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;

        if (action.payload?.twofa_required) {
          state.twofaRequired = true;
          state.pendingUserId = action.payload.user_id;
          state.pendingEmail = action.payload.email;

          // Clear any previously stored credentials
          localStorage.removeItem("authToken");
          localStorage.removeItem("user");

          state.isAuthenticated = false;
          return;
        }

        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        clearImpersonationState(state);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Register
      .addCase(registerUser.pending, (state) => {
        state.isRegisterLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.isRegisterLoading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isRegisterLoading = false;
        state.error = action.payload;
      })

      // profile update
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        let dataToMerge = action.payload;
        if (action.payload?.requires_otp) {
          const { twofa_status, ...rest } = action.payload;
          dataToMerge = rest;
        }
        state.user = { ...state.user, ...dataToMerge };
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // profile complete update
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;

        // Update each field explicitly from the nested user object
        const userData = action.payload?.user || action.payload;
        if (userData) {
          state.user = {
            ...state.user,
            id: userData.id || state.user.id,
            first_name: userData.first_name || state.user.first_name,
            last_name: userData.last_name || state.user.last_name,
            display_name: userData.display_name || state.user.display_name,
            email: userData.email || state.user.email,
            dob: userData.dob || state.user.dob,
            gender: userData.gender || state.user.gender,
            phone: userData.phone || state.user.phone,
            location: userData.location || state.user.location,
            user_coordinates:
              userData.user_coordinates || state.user.user_coordinates,
            avatar: userData.avatar || state.user.avatar,
            native_language:
              userData.native_language || state.user.native_language,
            updated_at: userData.updated_at || state.user.updated_at,
            // Include other fields as needed
            is_profile_completed: true,
          };
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Privacy update
      .addCase(updatePrivacy.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePrivacy.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.privacy = action.payload;
        }
      })
      .addCase(updatePrivacy.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Notification update
      .addCase(updateNotificationSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.privacy = action.payload;
        }
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // social with login
      .addCase(socialLogin.pending, (state, action) => {
        state.isSocialLogin = true;
        state.socialLoginProvider = action.meta.arg.provider; // Set the provider here
        state.error = null;
      })
      .addCase(socialLogin.fulfilled, (state, action) => {
        state.isSocialLogin = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.socialLoginProvider = null;
        clearImpersonationState(state);
      })
      .addCase(socialLogin.rejected, (state, action) => {
        state.isSocialLogin = false;
        state.error = action.payload;
        state.socialLoginProvider = null;
      });
  },
});

export const {
  setActiveForm,
  clearErrors,
  logout,
  resetFormState,
  rehydrate,
  setLocationUpdated,
  setCountsUpdated,
  setAdminCountsUpdated,
  setUser,
  setImpersonating,
  setBusinessAccount,
  loginUserSuccess,
  setAdminVisibility,
} = authSlice.actions;
export default authSlice.reducer;
